import React from "react";
import moment from "moment";
import type { ColumnsType } from "antd/es/table";
import { Table, Tooltip, Tag } from "antd";

import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import type { LeadHistory, Pagination } from "../../../../../core/data/redux/leads-queue/types";

interface LeadsHistoryTableProps {
    leadsHistory: LeadHistory[];
    pagination: Pagination;
    tableLoading: boolean;
    showFormColumn?: boolean;
    onPageChange: (page: number) => void | Promise<void>;
}

const LeadsHistoryTable: React.FC<LeadsHistoryTableProps> = ({
    leadsHistory,
    pagination,
    tableLoading,
    showFormColumn = false,
    onPageChange,
}) => {
    const calculateTimeInQueue = (enteredAt: string, assignedAt: string) => {
        const entered = moment(enteredAt);
        const assigned = moment(assignedAt);
        const duration = moment.duration(assigned.diff(entered));

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const columns: ColumnsType<LeadHistory> = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 120,
            render: (text) => <span className="text-muted">#{text}</span>,
            sorter: (a, b) => a.id - b.id,
        },
        {
            title: "Lead Name",
            dataIndex: "LeadName",
            key: "LeadName",
            width: 200,
            render: (text) => (
                <div className="d-flex align-items-center">
                    <h6 className="fw-medium fs-14 table-text-wrap mb-0">{text}</h6>
                </div>
            ),
            sorter: (a, b) => a.LeadName.localeCompare(b.LeadName),
        },
        {
            title: "Email",
            dataIndex: "Email",
            key: "Email",
            width: 230,
            ellipsis: { showTitle: false },
            render: (text) => (
                <Tooltip placement="topLeft" title={text || "N/A"}>
                    <span className="text-muted">{text || "N/A"}</span>
                </Tooltip>
            ),
            sorter: (a, b) => (a.Email || "").localeCompare(b.Email || ""),
        },
        {
            title: "Phone",
            dataIndex: "Phone",
            key: "Phone",
            width: 160,
            render: (text) => (
                <span className="text-nowrap">
                    <i className="ti ti-phone me-2 text-muted" />
                    {text}
                </span>
            ),
            sorter: (a, b) => a.Phone.localeCompare(b.Phone),
        },
        {
            title: "Lead Source",
            dataIndex: "lead_source_child",
            key: "lead_source_child",
            width: 180,
            render: (text) => (
                <span className="badge badge-soft-secondary w-100">{text || "N/A"}</span>
            ),
            sorter: (a, b) =>
                (a.lead_source_child || "").localeCompare(b.lead_source_child || ""),
        },
        ...(showFormColumn
            ? [
                {
                    title: "Form",
                    dataIndex: "form_name",
                    key: "form_name",
                    width: 200,
                    render: (text: string | null) => (
                        <span className="badge badge-soft-info w-100">{text || "N/A"}</span>
                    ),
                    sorter: (a: LeadHistory, b: LeadHistory) =>
                        (a.form_name || "").localeCompare(b.form_name || ""),
                },
            ]
            : []),
        {
            title: "Entered Queue",
            dataIndex: "CreatedDate",
            key: "CreatedDate",
            width: 185,
            render: (date) => (
                <Tooltip title={humanReadableDate(date, true, undefined, undefined, false)}>
                    <span className="text-muted small">
                        <i className="ti ti-login me-1" />
                        {humanReadableDate(date, true, undefined, undefined, false)}
                    </span>
                </Tooltip>
            ),
            sorter: (a, b) =>
                new Date(a.CreatedDate).getTime() - new Date(b.CreatedDate).getTime(),
        },
        {
            title: "Assigned To",
            dataIndex: "AssignedTo",
            key: "AssignedTo",
            width: 150,
            render: (assignedTo) => {
                if (!assignedTo || assignedTo === "Unknown") {
                    return <Tag color="default">Not Assigned</Tag>;
                }
                return (
                    <Tooltip title={assignedTo}>
                        <Tag color="blue">
                            <i className="ti ti-user me-1" />
                            {assignedTo}
                        </Tag>
                    </Tooltip>
                );
            },
            sorter: (a, b) => (a.AssignedTo || "").localeCompare(b.AssignedTo || ""),
        },
        {
            title: "Time in Queue",
            dataIndex: "time_in_queue",
            key: "time_in_queue",
            width: 150,
            render: (_, record) => {
                if (!record.AssignedDate) {
                    return (
                        <Tooltip title="This lead was assigned before assignment times were recorded">
                            <Tag color="default">Not recorded</Tag>
                        </Tooltip>
                    );
                }
                const timeInQueue = calculateTimeInQueue(record.CreatedDate, record.AssignedDate);
                return (
                    <Tooltip
                        title={`Assigned at ${humanReadableDate(
                            record.AssignedDate,
                            true,
                            undefined,
                            undefined,
                            false,
                        )}`}
                    >
                        <Tag color="green">
                            <i className="ti ti-clock me-1" />
                            {timeInQueue}
                        </Tag>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                if (!a.AssignedDate) return 1;
                if (!b.AssignedDate) return -1;
                const timeA = moment(a.AssignedDate).diff(moment(a.CreatedDate));
                const timeB = moment(b.AssignedDate).diff(moment(b.CreatedDate));
                return timeA - timeB;
            },
        },
    ];

    return (
        <div className="card-body p-0 mb-3">
            {tableLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                    <div className="text-center">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading history...</p>
                    </div>
                </div>
            ) : leadsHistory.length > 0 ? (
                <Table
                    className="datanew dataTable no-footer"
                    columns={columns}
                    dataSource={leadsHistory}
                    rowKey="id"
                    rowHoverable={false}
                    pagination={{
                        current: pagination.current_page,
                        pageSize: pagination.per_page,
                        total: pagination.total,
                        onChange: (page) => onPageChange(page),
                        showTotal: (total: number, range: [number, number]) =>
                            `Showing ${range[0]} - ${range[1]} of ${total} entries`,
                        showSizeChanger: false,
                    }}
                    scroll={{ x: 1400 }}
                />
            ) : (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="ti ti-history-off text-muted" style={{ fontSize: "4rem" }} />
                        </div>
                        <h5 className="mt-3 mb-2 fw-semibold">No History Found</h5>
                        <p className="text-muted mb-0">
                            No lead history available for the selected filters
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadsHistoryTable;
